import { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Loader2, Lock, ArrowRight, MessageSquare, Calendar, Send } from 'lucide-react';

interface InterviewModeConfig {
  systemPrompt: string;
  opener: string;
  suggestedPrompts?: string[];
  maxTurns?: number;
}

interface NeraInlineViewerProps {
  intro?: string | null;
  /** If provided, runs in interview mode with a custom system prompt and opening message */
  interviewMode?: InterviewModeConfig;
  /** If provided, loads interview config from the interview_campaigns + interview_modules tables.
   *  Takes precedence over interviewMode if both are set. */
  campaignSlug?: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

// Suggested prompts should lead to direct, closed-end answers — not open-ended
// follow-up questions. Each one should map to a specific piece of content on the site.
const SUGGESTED_PROMPTS = [
  'Explain the four stages of the DAIS methodology',
  'What does Transformation Staging actually involve?',
  'What is a knowledge platform?',
];

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const NERA_API_URL =
  import.meta.env.VITE_NERA_API_URL ||
  `${SUPABASE_URL || 'https://ksfdabyledggbeweeqta.supabase.co'}/functions/v1/nera-query`;

// Client-side rate limit per viewer mode. Knowledge Q&A and each campaign
// get their own bucket so a visitor who explores Q&A still has a full
// turn budget left for the Conversational Survey on the same page.
// Real abuse protection happens server-side via the EnquiryIntake
// magic-link flow for anyone who wants more depth.
const RATE_LIMIT_QA_MAX = 3;
const RATE_LIMIT_WINDOW_MS = 10 * 60 * 1000; // 10 minutes
const RATE_LIMIT_KEY_PREFIX = 'carlorbiz_nera_public_usage';

const SESSION_ID_KEY = 'carlorbiz_nera_session_id';

interface RateLimitState {
  count: number;
  windowStart: number;
}

function rateLimitKey(bucket: string) {
  return `${RATE_LIMIT_KEY_PREFIX}_${bucket}`;
}

function getRateLimitState(bucket: string): RateLimitState {
  try {
    const raw = localStorage.getItem(rateLimitKey(bucket));
    if (!raw) return { count: 0, windowStart: Date.now() };
    const parsed = JSON.parse(raw) as RateLimitState;
    // Reset window if expired
    if (Date.now() - parsed.windowStart > RATE_LIMIT_WINDOW_MS) {
      return { count: 0, windowStart: Date.now() };
    }
    return parsed;
  } catch {
    return { count: 0, windowStart: Date.now() };
  }
}

function incrementRateLimit(bucket: string): RateLimitState {
  const state = getRateLimitState(bucket);
  const newState = { count: state.count + 1, windowStart: state.windowStart };
  try { localStorage.setItem(rateLimitKey(bucket), JSON.stringify(newState)); } catch { /* noop */ }
  return newState;
}

function getOrCreateSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;
    const newId = `public-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem(SESSION_ID_KEY, newId);
    return newId;
  } catch {
    return `public-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }
}

function NeraSoftGate({ isInterviewMode }: { isInterviewMode?: boolean }) {
  if (isInterviewMode) {
    // Interview/survey mode: no gate, just end the conversation
    return null;
  }

  return (
    <div className="mt-6 rounded-xl border-2 border-[var(--color-brand-accent)] bg-gradient-to-br from-[var(--color-brand-accent)]/5 to-transparent p-6 space-y-5">
      <div className="space-y-3">
        <p className="font-heading font-bold text-lg text-foreground">
          That was a fixed-path conversation.
        </p>
        <p className="text-sm text-muted-foreground leading-relaxed">
          The full version doesn&rsquo;t follow a script &mdash; Nera reads your response, decides which thread to follow, and goes where the insight is. Every conversation is different. Every insight card reflects you.
        </p>
        <p className="text-sm font-medium text-foreground">
          Three scenarios to explore: strategic direction, team dynamics, or impact and evidence.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <a
          href="/contact"
          className="group flex items-center gap-3 rounded-lg border border-[var(--color-brand-primary)]/20 bg-white p-4 hover:border-[var(--color-brand-primary)] hover:shadow-md transition-all"
        >
          <MessageSquare className="h-5 w-5 text-[var(--color-brand-primary)] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-sm text-[var(--color-brand-dark)]">Start a conversation with Carla</p>
            <p className="text-xs text-muted-foreground">Tell me what you&rsquo;re working on</p>
          </div>
          <ArrowRight className="h-4 w-4 text-[var(--color-brand-primary)] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
        </a>
        <a
          href="/services/what-will-it-cost"
          className="group flex items-center gap-3 rounded-lg border border-[var(--color-brand-accent)]/30 bg-white p-4 hover:border-[var(--color-brand-accent)] hover:shadow-md transition-all"
        >
          <Calendar className="h-5 w-5 text-[var(--color-brand-accent)] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-sm text-[var(--color-brand-dark)]">Explore pricing</p>
            <p className="text-xs text-muted-foreground">Walk through your options with Nera</p>
          </div>
          <ArrowRight className="h-4 w-4 text-[var(--color-brand-accent)] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
        </a>
      </div>
    </div>
  );
}

export function NeraInlineViewer({ intro, interviewMode, campaignSlug }: NeraInlineViewerProps) {
  const [input, setInput] = useState('');
  // Campaign-loaded interview config (from DB, replaces inline interviewMode)
  const [campaignConfig, setCampaignConfig] = useState<InterviewModeConfig | null>(null);
  const [campaignLoading, setCampaignLoading] = useState(!!campaignSlug);

  // Load campaign config from Supabase on mount
  useEffect(() => {
    if (!campaignSlug) return;
    const loadCampaign = async () => {
      try {
        const { createClient } = await import('@supabase/supabase-js');
        const sb = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
        const { data: campaign } = await sb
          .from('interview_campaigns')
          .select('module_slug, campaign_overrides')
          .eq('slug', campaignSlug)
          .eq('is_active', true)
          .single();
        if (!campaign) { setCampaignLoading(false); return; }

        const { data: mod } = await sb
          .from('interview_modules')
          .select('system_prompt, opener, suggested_prompts, max_turns')
          .eq('slug', campaign.module_slug)
          .eq('is_active', true)
          .single();
        if (mod) {
          setCampaignConfig({
            systemPrompt: mod.system_prompt,
            opener: mod.opener,
            suggestedPrompts: mod.suggested_prompts || [],
            maxTurns: mod.max_turns ?? undefined,
          });
        }
      } catch (e) {
        console.error('Failed to load campaign config:', e);
      } finally {
        setCampaignLoading(false);
      }
    };
    loadCampaign();
  }, [campaignSlug]);

  // Resolved interview config: campaignConfig (DB) > interviewMode (inline prop)
  const resolvedInterview = campaignConfig || interviewMode || null;

  // Interview mode: pre-populate with opener message
  const [messages, setMessages] = useState<ChatMessage[]>(
    interviewMode?.opener ? [{ role: 'assistant', content: interviewMode.opener }] : []
  );
  // When campaign config loads async, set the opener message
  useEffect(() => {
    if (campaignConfig?.opener && messages.length === 0) {
      setMessages([{ role: 'assistant', content: campaignConfig.opener }]);
    }
  }, [campaignConfig]);

  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-mode rate limiting: Q&A and each campaign get separate buckets so
  // visiting both demos on one page doesn't drain a shared turn budget.
  const rateLimitBucket = campaignSlug ? `survey_${campaignSlug}` : 'qa';
  const rateLimitMax = resolvedInterview?.maxTurns ?? RATE_LIMIT_QA_MAX;
  const [remainingQueries, setRemainingQueries] = useState(rateLimitMax);
  // Interview mode gets its own session ID to keep conversations separate
  const [sessionId] = useState<string>(
    (interviewMode || campaignSlug) ? `interview-${Date.now()}-${Math.random().toString(36).slice(2, 9)}` : getOrCreateSessionId()
  );
  const threadEndRef = useRef<HTMLDivElement>(null);
  const threadContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const activeSuggestions = resolvedInterview?.suggestedPrompts ?? SUGGESTED_PROMPTS;
  const hasAsked = messages.some((m) => m.role === 'user');
  const limitReached = error === 'limit-reached' || remainingQueries === 0;

  // Hydrate rate limit state from localStorage. Re-runs when the bucket or
  // cap changes (i.e. once campaign config has loaded async and bumped
  // rateLimitMax from the Q&A default to the campaign's max_turns).
  useEffect(() => {
    const state = getRateLimitState(rateLimitBucket);
    setRemainingQueries(Math.max(0, rateLimitMax - state.count));
  }, [rateLimitBucket, rateLimitMax]);

  // Auto-scroll within the thread container only — never scroll the page itself.
  // This keeps the accordion and Nera's response visible while streaming.
  useEffect(() => {
    const container = threadContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, isStreaming]);

  // Focus input after streaming completes
  useEffect(() => {
    if (!isStreaming && hasAsked && !limitReached) {
      inputRef.current?.focus();
    }
  }, [isStreaming, hasAsked, limitReached]);

  const submit = useCallback(async (query: string) => {
    if (!query.trim() || isStreaming) return;

    // Rate limit check (per-mode bucket)
    const state = getRateLimitState(rateLimitBucket);
    if (state.count >= rateLimitMax) {
      setError('limit-reached');
      return;
    }

    const userMessage: ChatMessage = { role: 'user', content: query.trim() };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsStreaming(true);
    setError(null);

    // Add a placeholder assistant message that we will stream into
    const assistantIndex = messages.length + 1; // index after user message is appended
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    try {
      const res = await fetch(NERA_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          query,
          session_id: sessionId,
          public: true,
          // Campaign-slug path (DB-driven): send slug, let the edge function load the module
          ...(campaignSlug ? {
            campaign_slug: campaignSlug,
            conversation_history: messages
              .filter(m => m.content)
              .map(m => ({ role: m.role, content: m.content })),
          }
          // No inline-prompt path any more (CC-429, 27 Sep 2026): the edge function
          // ignores system_prompt_override. An interview without a campaign slug
          // runs on Nera's default prompt; the opener still comes from the config.
          : {}),
        }),
      });

      if (!res.ok) {
        setError(`Sorry, I couldn't answer that right now (${res.status}).`);
        // Remove the empty assistant placeholder
        setMessages((prev) => prev.filter((_, i) => i !== assistantIndex));
        setIsStreaming(false);
        return;
      }

      const contentType = res.headers.get('content-type') || '';

      if (contentType.includes('application/json')) {
        const data = await res.json();
        setMessages((prev) =>
          prev.map((m, i) =>
            i === assistantIndex ? { ...m, content: data.answer || '' } : m
          )
        );
        setIsStreaming(false);
        const newState = incrementRateLimit(rateLimitBucket);
        setRemainingQueries(Math.max(0, rateLimitMax - newState.count));
        return;
      }

      // SSE stream
      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = '';
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6);
            try {
              const parsed = JSON.parse(jsonStr);
              if (currentEvent === 'delta' && parsed.text) {
                accumulated += parsed.text;
                setMessages((prev) =>
                  prev.map((m, i) =>
                    i === assistantIndex ? { ...m, content: accumulated } : m
                  )
                );
              } else if (currentEvent === 'meta' && parsed.answer) {
                accumulated = parsed.answer;
                setMessages((prev) =>
                  prev.map((m, i) =>
                    i === assistantIndex ? { ...m, content: accumulated } : m
                  )
                );
              } else if (currentEvent === 'error') {
                setError(parsed.message || 'Stream error');
              }
            } catch {
              // skip malformed
            }
          }
        }
      }

      setIsStreaming(false);

      // Successful query -> increment counter
      const newState = incrementRateLimit(rateLimitBucket);
      setRemainingQueries(Math.max(0, rateLimitMax - newState.count));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      // Remove empty assistant placeholder on error
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && !last.content) {
          return prev.slice(0, -1);
        }
        return prev;
      });
      setIsStreaming(false);
    }
  }, [isStreaming, messages.length, sessionId, rateLimitBucket, rateLimitMax, campaignSlug, resolvedInterview]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submit(input);
  };

  const handleSuggestion = (prompt: string) => {
    setInput(prompt);
    submit(prompt);
  };

  // Show loading state while campaign config is being fetched
  if (campaignLoading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 flex items-center gap-2 text-muted-foreground text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading conversation...</span>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Intro text */}
      {intro && (
        <div className="prose prose-sm max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{intro}</ReactMarkdown>
        </div>
      )}

      {/* Gold divider between intro and interaction area */}
      {intro && (
        <div className="h-px bg-gradient-to-r from-transparent via-[#D5B13A]/40 to-transparent my-6" />
      )}

      {/* Suggested prompts — only before first question */}
      {!hasAsked && !resolvedInterview && (
        <>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about Carlorbiz services, methodology, or pricing..."
              disabled={isStreaming}
              className="flex-1 border-2 border-border rounded-md px-4 py-3 focus:border-[var(--color-brand-accent)] outline-none disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={isStreaming || !input.trim()}
              className="bg-[image:var(--gradient-accent)] text-white font-semibold px-6 py-3 rounded-full hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              Ask Nera
            </button>
          </form>

          <div className="mt-4 flex flex-wrap gap-2">
            {activeSuggestions.map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => handleSuggestion(prompt)}
                disabled={isStreaming}
                className="bg-muted hover:bg-[var(--color-brand-accent)]/10 border border-border rounded-full px-3 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {prompt}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Conversation thread */}
      {messages.length > 0 && (
        <div ref={threadContainerRef} className="mt-4 space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-[var(--color-brand-primary)] text-white rounded-br-sm'
                    : 'bg-muted border border-border rounded-bl-sm'
                }`}
              >
                {msg.role === 'user' ? (
                  <p className="text-sm leading-relaxed">{msg.content}</p>
                ) : msg.content ? (
                  <div className="prose prose-sm max-w-none">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                ) : null}
              </div>
            </div>
          ))}

          {/* "Nera is thinking..." indicator during streaming */}
          {isStreaming && messages[messages.length - 1]?.role === 'assistant' && !messages[messages.length - 1]?.content && (
            <div className="flex justify-start">
              <div className="bg-muted border border-border rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-2 text-muted-foreground text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{resolvedInterview && messages.filter(m => m.role === 'user').length >= 3
                  ? 'Compiling insights from our conversation...'
                  : resolvedInterview
                    ? 'Considering your response...'
                    : 'Nera is thinking...'
                }</span>
              </div>
            </div>
          )}

          <div ref={threadEndRef} />
        </div>
      )}

      {/* Error display (non-limit errors) */}
      {error && error !== 'limit-reached' && (
        <div className="mt-4 bg-destructive/10 border border-destructive/30 rounded-md p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Interview mode: input with suggested responses shown after opener */}
      {resolvedInterview && !hasAsked && !limitReached && (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {activeSuggestions.map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => handleSuggestion(prompt)}
                disabled={isStreaming}
                className="bg-muted hover:bg-[var(--color-brand-accent)]/10 border border-border rounded-full px-3 py-1.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-left"
              >
                {prompt}
              </button>
            ))}
          </div>
          <form onSubmit={handleSubmit} className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Or type your own response..."
              disabled={isStreaming}
              className="flex-1 border-2 border-border rounded-md px-4 py-2.5 text-sm focus:border-[var(--color-brand-accent)] outline-none disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={isStreaming || !input.trim()}
              className="bg-[image:var(--gradient-accent)] text-white px-4 py-2.5 rounded-full hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}

      {/* Follow-up input — shown after first question, when not at limit */}
      {hasAsked && !limitReached && (
        <div className="mt-4">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={resolvedInterview ? "Type your response..." : "Ask a follow-up..."}
              disabled={isStreaming}
              className="flex-1 border-2 border-border rounded-md px-4 py-2.5 text-sm focus:border-[var(--color-brand-accent)] outline-none disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={isStreaming || !input.trim()}
              className="bg-[image:var(--gradient-accent)] text-white px-4 py-2.5 rounded-full hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              aria-label="Send"
            >
              {isStreaming ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </button>
          </form>
          <p className="mt-1.5 text-xs text-muted-foreground">
            {remainingQueries} {remainingQueries === 1 ? 'question' : 'questions'} remaining
          </p>
        </div>
      )}

      {/* Soft gate when limit is reached */}
      {limitReached && <NeraSoftGate isInterviewMode={!!resolvedInterview} />}
    </div>
  );
}
